import { apiFetch } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'

const API_ROOT = import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'
const API_BASE = `${API_ROOT}/v1`

/**
 * The signed-in user's profile: a flat map of string values the UI keeps for
 * itself, stored on the server so it follows the user between browsers.
 *
 * Every value is a string. Structured data goes in as JSON and comes back out
 * the same way — the same convention the old UI uses, so the two can share a
 * profile without tripping over each other's keys.
 */
interface UserProfileResponse {
  username: string
  profile: Record<string, string | null> | null
}

export const UserProfileApi = {
  /** Every key in the profile, values still as the strings they are stored as. */
  getAll: async (): Promise<Record<string, string>> => {
    const res = await apiFetch(`${API_BASE}/userprofiles`)
    if (!res.ok) await throwApiError(res, 'Failed to load user profile')
    const data = (await res.json()) as UserProfileResponse
    return Object.fromEntries(
      Object.entries(data.profile ?? {}).filter((entry): entry is [string, string] => entry[1] != null)
    )
  },

  /**
   * Stores one or more keys as JSON in a single request. The server merges
   * them into the profile, leaving every other key alone; `null` removes a key.
   */
  setMany: async (userId: string, values: Record<string, unknown>): Promise<void> => {
    const profile = Object.fromEntries(
      Object.entries(values).map(([key, value]) => [key, value === null ? null : JSON.stringify(value)])
    )
    const res = await apiFetch(`${API_BASE}/userprofiles/${encodeURIComponent(userId)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ profile }),
    })
    if (!res.ok) await throwApiError(res, 'Failed to save user profile')
  },
}

/** One stored value parsed from JSON; `fallback` when unset or unreadable. */
export function parseProfileValue<T>(raw: string | undefined, fallback: T): T {
  if (raw === undefined) return fallback
  try {
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}
